import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { authHeaders } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  BarChart3, Clock, Users, MessageSquare, FolderOpen, Truck, Wallet,
  CheckCircle2, Circle, Loader2, UserPlus, MapPin, Calendar,
  FileText, Image, Video, Music, Archive, Shield
} from 'lucide-react';
import { demoProjects, demoTasks, demoFiles, demoPayments, demoEvents, recentActivity } from '../data/dummyData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 8 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

const fileIcon = (type) => {
  const icons = { video: Video, audio: Music, design: Image, archive: Archive };
  return icons[type] || FileText;
};

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <motion.div variants={fadeUp} className="mb-6">
      <div className="flex items-center gap-2.5">
        <Icon className="w-5 h-5 text-foreground" strokeWidth={1.5} />
        <div>
          <h2 className="text-lg font-semibold tracking-tight font-heading">{title}</h2>
          {subtitle && <p className="text-[11px] text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
      </div>
    </motion.div>
  );
}

function AnalyticsSection({ project, tasks }) {
  const done = tasks.filter(t => t.status === 'done').length;
  const inProg = tasks.filter(t => t.status === 'in-progress').length;
  const todo = tasks.filter(t => t.status === 'todo').length;
  const total = tasks.length || 1;
  const budgetUsed = project.progress ? Math.round(project.budget * project.progress / 100) : 0;

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <SectionHeader icon={BarChart3} title="Analytics" subtitle="Project performance and metrics" />

      <motion.div variants={fadeUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Progress', value: `${project.progress || 0}%`, sub: 'overall' },
          { label: 'Tasks Done', value: `${done}/${total}`, sub: `${inProg} in progress` },
          { label: 'Budget', value: project.budget ? `₹${(project.budget / 100000).toFixed(1)}L` : '--', sub: `₹${(budgetUsed / 100000).toFixed(1)}L used` },
          { label: 'Team', value: project.team_size || 0, sub: 'members' },
        ].map(s => (
          <div key={s.label} className="p-4 rounded-xl border border-border/50 bg-card" data-testid={`analytics-${s.label.toLowerCase()}`}>
            <p className="font-heading text-xl font-semibold">{s.value}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
            <p className="text-[9px] text-muted-foreground/50">{s.sub}</p>
          </div>
        ))}
      </motion.div>

      {/* Task breakdown bar */}
      <motion.div variants={fadeUp} className="mb-6">
        <p className="text-[11px] text-muted-foreground mb-2 font-medium">Task Breakdown</p>
        <div className="flex h-3 rounded-full overflow-hidden bg-secondary">
          {done > 0 && <div className="bg-foreground transition-all" style={{ width: `${(done / total) * 100}%` }} />}
          {inProg > 0 && <div className="bg-[hsl(36,80%,52%)] transition-all" style={{ width: `${(inProg / total) * 100}%` }} />}
          {todo > 0 && <div className="bg-muted-foreground/20 transition-all" style={{ width: `${(todo / total) * 100}%` }} />}
        </div>
        <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-foreground" /> Done ({done})</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-[hsl(36,80%,52%)]" /> In Progress ({inProg})</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-muted-foreground/20" /> Todo ({todo})</span>
        </div>
      </motion.div>

      {/* Timeline */}
      <motion.div variants={fadeUp}>
        <p className="text-[11px] text-muted-foreground mb-2 font-medium">Timeline</p>
        <div className="p-4 rounded-xl border border-border/50 bg-card">
          <div className="flex justify-between text-[11px]">
            <span className="text-muted-foreground">Start: <span className="text-foreground font-medium">{project.timeline_start || '--'}</span></span>
            <span className="text-muted-foreground">End: <span className="text-foreground font-medium">{project.timeline_end || '--'}</span></span>
          </div>
          <div className="w-full h-2 rounded-full bg-secondary mt-3">
            <div className="h-full rounded-full bg-[hsl(36,80%,52%)]" style={{ width: `${project.progress || 0}%` }} />
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function RecentsSection({ projectId }) {
  const projectActivity = recentActivity.filter(a => {
    const proj = demoProjects.find(p => p.project_id === projectId);
    return proj && a.project === proj.name;
  });

  const events = demoEvents.filter(e => e.project_id === projectId).slice(0, 5);

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <SectionHeader icon={Clock} title="Recents" subtitle="Latest activity on this project" />

      {projectActivity.length > 0 && (
        <motion.div variants={fadeUp} className="mb-6">
          <p className="text-[11px] text-muted-foreground mb-2 font-medium">Activity</p>
          <div className="space-y-1">
            {projectActivity.map(a => (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-secondary/60 transition-colors" data-testid={`recent-activity-${a.id}`}>
                <Clock className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" strokeWidth={1.5} />
                <span className="text-sm flex-1">{a.action}</span>
                <span className="text-[10px] text-muted-foreground/50">{a.time}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <motion.div variants={fadeUp}>
        <p className="text-[11px] text-muted-foreground mb-2 font-medium">Upcoming Events</p>
        {events.length > 0 ? (
          <div className="space-y-2">
            {events.map(evt => (
              <div key={evt.event_id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card" data-testid={`event-${evt.event_id}`}>
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{evt.title}</p>
                  <p className="text-[10px] text-muted-foreground">{evt.date} {evt.time && `at ${evt.time}`}</p>
                </div>
                <Badge variant="secondary" className="text-[9px] h-4 px-1.5 capitalize">{evt.event_type}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-8">No upcoming events</p>
        )}
      </motion.div>
    </motion.div>
  );
}

function TeamsSection({ projectId }) {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTeam = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/team`, { headers: authHeaders() });
      if (res.ok) setTeam(await res.json());
    } catch {} finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <SectionHeader icon={Users} title="Teams" subtitle="Project team members and collaborators" />

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : team.length > 0 ? (
        <motion.div variants={fadeUp} className="space-y-2">
          {team.map(m => (
            <div key={m.invite_id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card group" data-testid={`team-${m.invite_id}`}>
              <img
                src={m.avatar}
                alt={m.name}
                className="w-9 h-9 rounded-full object-cover bg-secondary flex-shrink-0"
                onError={e => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=e5e5e5&color=333&size=80`; }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{m.name}</p>
                  <Badge variant="secondary" className="text-[9px] h-4 px-1.5 capitalize">{m.status}</Badge>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  <span className="font-mono">{m.ainaa_id}</span>
                  <span className="mx-1.5 text-border">·</span>
                  <span>{m.role || m.person_role}</span>
                </p>
              </div>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[hsl(36,80%,52%)]/15 text-[hsl(36,80%,42%)] text-[9px] font-medium">
                <Shield className="w-2.5 h-2.5" strokeWidth={2.5} />{m.trust_score}
              </span>
            </div>
          ))}
        </motion.div>
      ) : (
        <motion.div variants={fadeUp} className="text-center py-12 border border-dashed border-border rounded-xl">
          <Users className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground mb-1">No team members yet</p>
          <p className="text-xs text-muted-foreground mb-4">Invite collaborators from the AINAA network</p>
          <Button size="sm" className="h-8 text-xs gap-1.5" asChild>
            <Link to={`/projects/${projectId}`}><UserPlus className="w-3.5 h-3.5" strokeWidth={2} /> Go to Overview to Invite</Link>
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}

function ConversationsSection() {
  const threads = [
    { id: 1, from: 'Arjun Mehta', msg: 'The night sequence locations look great. Should we do a recce this week?', time: '2h ago', unread: true },
    { id: 2, from: 'Priya Sharma', msg: 'Edit V2 is ready for review. Added the color-graded sequences.', time: '5h ago', unread: true },
    { id: 3, from: 'Vikram Reddy', msg: 'Budget revision approved. We have the extra 3L for equipment rental.', time: '1d ago', unread: false },
    { id: 4, from: 'Aisha Khan', msg: 'Location permits for South Mumbai confirmed for March 20-22.', time: '2d ago', unread: false },
    { id: 5, from: 'Meera Joshi', msg: 'Set design mockups attached. Let me know if the color palette works.', time: '3d ago', unread: false },
  ];

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <SectionHeader icon={MessageSquare} title="Conversations" subtitle="Project discussions and threads" />
      <motion.div variants={fadeUp} className="space-y-1">
        {threads.map(t => (
          <div
            key={t.id}
            className={`flex items-start gap-3 px-4 py-3 rounded-lg hover:bg-secondary/60 transition-colors cursor-pointer ${t.unread ? 'bg-secondary/30' : ''}`}
            data-testid={`conversation-${t.id}`}
          >
            <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-[11px] font-semibold flex-shrink-0 mt-0.5">
              {t.from[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className={`text-sm ${t.unread ? 'font-semibold' : 'font-medium'}`}>{t.from}</p>
                <span className="text-[10px] text-muted-foreground/50">{t.time}</span>
              </div>
              <p className={`text-[12px] truncate ${t.unread ? 'text-foreground' : 'text-muted-foreground'}`}>{t.msg}</p>
            </div>
            {t.unread && <span className="w-2 h-2 rounded-full bg-[hsl(36,80%,52%)] flex-shrink-0 mt-2" />}
          </div>
        ))}
      </motion.div>
    </motion.div>
  );
}

function AssetsSection({ projectId }) {
  const files = demoFiles.filter(f => f.project_id === projectId);

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <SectionHeader icon={FolderOpen} title="Assets" subtitle="Files, media, and documents" />
      {files.length > 0 ? (
        <motion.div variants={fadeUp} className="space-y-2">
          {files.map(f => {
            const Icon = fileIcon(f.file_type);
            return (
              <div key={f.file_id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card" data-testid={`asset-${f.file_id}`}>
                <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{f.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {f.uploaded_by} · {f.size}
                    {f.ainaa_id && <span className="ml-1.5 font-mono">{f.ainaa_id}</span>}
                  </p>
                </div>
                <span className="text-[10px] text-muted-foreground flex-shrink-0">{f.created_at}</span>
              </div>
            );
          })}
        </motion.div>
      ) : (
        <motion.div variants={fadeUp} className="text-center py-12">
          <p className="text-sm text-muted-foreground">No assets uploaded yet</p>
        </motion.div>
      )}
    </motion.div>
  );
}

function LogisticsSection({ project }) {
  const events = demoEvents.filter(e => e.project_id === project.project_id);
  const locations = [
    { name: 'Andheri Film City Studio 7', type: 'Indoor', status: 'Confirmed', dates: 'Mar 20-22' },
    { name: 'Bandra Bandstand Promenade', type: 'Outdoor', status: 'Pending Permit', dates: 'Mar 25' },
    { name: 'Colaba Heritage District', type: 'Outdoor', status: 'Scouting', dates: 'TBD' },
  ];

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <SectionHeader icon={Truck} title="Logistics" subtitle="Scheduling, locations, and planning" />

      <motion.div variants={fadeUp} className="mb-6">
        <p className="text-[11px] text-muted-foreground mb-2 font-medium">Schedule</p>
        {events.length > 0 ? (
          <div className="space-y-2">
            {events.map(evt => (
              <div key={evt.event_id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card" data-testid={`logistics-event-${evt.event_id}`}>
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{evt.title}</p>
                  <p className="text-[10px] text-muted-foreground">{evt.date} {evt.time && `· ${evt.time}`}</p>
                </div>
                <Badge variant="secondary" className="text-[9px] h-4 px-1.5 capitalize">{evt.event_type}</Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">No events scheduled</p>
        )}
      </motion.div>

      <motion.div variants={fadeUp}>
        <p className="text-[11px] text-muted-foreground mb-2 font-medium">Locations</p>
        <div className="space-y-2">
          {locations.map((loc, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-card" data-testid={`location-${i}`}>
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{loc.name}</p>
                <p className="text-[10px] text-muted-foreground">{loc.type} · {loc.dates}</p>
              </div>
              <Badge
                variant={loc.status === 'Confirmed' ? 'default' : 'secondary'}
                className="text-[9px] h-4 px-1.5"
              >
                {loc.status}
              </Badge>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

function AccountsSection({ projectId, project }) {
  const payments = demoPayments.filter(p => p.project_id === projectId);
  const paid = payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0);
  const pending = payments.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0);
  const overdue = payments.filter(p => p.status === 'overdue').reduce((s, p) => s + p.amount, 0);

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <SectionHeader icon={Wallet} title="Accounts" subtitle="Budget, payments, and invoices" />

      <motion.div variants={fadeUp} className="grid grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Paid', value: paid, color: 'text-emerald-600 dark:text-emerald-400' },
          { label: 'Pending', value: pending, color: 'text-amber-600 dark:text-amber-400' },
          { label: 'Overdue', value: overdue, color: 'text-red-600 dark:text-red-400' },
        ].map(t => (
          <div key={t.label} className="p-4 rounded-xl border border-border/50 bg-card" data-testid={`accounts-${t.label.toLowerCase()}`}>
            <p className="text-[10px] text-muted-foreground mb-1">{t.label}</p>
            <p className={`font-heading text-lg font-semibold ${t.color}`}>₹{(t.value / 100000).toFixed(1)}L</p>
          </div>
        ))}
      </motion.div>

      <motion.div variants={fadeUp}>
        <p className="text-[11px] text-muted-foreground mb-2 font-medium">Transactions</p>
        <div className="space-y-2">
          {payments.map(p => (
            <div key={p.payment_id} className="flex items-center gap-3 p-3.5 rounded-xl border border-border/50 bg-card" data-testid={`payment-${p.payment_id}`}>
              <Wallet className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{p.title}</p>
                <p className="text-[10px] text-muted-foreground">To {p.recipient} · Due {p.due_date}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-heading font-medium">₹{(p.amount / 1000).toFixed(0)}K</p>
                <Badge
                  variant={p.status === 'paid' ? 'default' : p.status === 'overdue' ? 'destructive' : 'secondary'}
                  className="text-[9px] h-4 px-1 capitalize"
                >
                  {p.status}
                </Badge>
              </div>
            </div>
          ))}
          {payments.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">No transactions yet</p>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function ProjectSectionPage({ section }) {
  const { id } = useParams();
  const [project, setProject] = useState(null);

  useEffect(() => {
    const demo = demoProjects.find(p => p.project_id === id);
    if (demo) setProject(demo);

    fetch(`${API}/projects/${id}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setProject(data); })
      .catch(() => {});
  }, [id]);

  if (!project) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const tasks = demoTasks.filter(t => t.project_id === id);

  const sections = {
    analytics: <AnalyticsSection project={project} tasks={tasks} />,
    recents: <RecentsSection projectId={id} />,
    teams: <TeamsSection projectId={id} />,
    conversations: <ConversationsSection />,
    assets: <AssetsSection projectId={id} />,
    logistics: <LogisticsSection project={project} />,
    accounts: <AccountsSection projectId={id} project={project} />,
  };

  return (
    <div data-testid={`project-section-${section}`}>
      {sections[section] || <p className="text-muted-foreground">Section not found</p>}
    </div>
  );
}
